import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import WebApp from "@twa-dev/sdk";
import axios from "axios";
import { API_URL } from "../config";
import { checkAndUpdateUserInfo } from "../utils/UpdateUserData";
import fpPromise from '@fingerprintjs/fingerprintjs';

const UserContext = createContext(null);

const defaultUserData = {
  username: "",
  firstName: "",
  lastName: "",
  userId: "",
  photo_url: "",
  isPremium: false,
  referralCode: "",
  inviteLink: "",
  user: null,
  channels: null,
};

export function UserProvider({ children }) {
  const [userData, setUserData] = useState(defaultUserData);
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [isTelegramMiniApp, setIsTelegramMiniApp] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const effectRan = useRef(false);
  const APIKey = process.env.REACT_APP_API_KEY;
  const wallet = useTonWallet();

  // Handle wallet linking natively inside the context wrapper
  useEffect(() => {
    const userId = userData.userId || userData.user?.userId;
    if (wallet && userId) {
      const address = wallet.account.address;
      axios.put(`${API_URL}/api/user/${userId}/wallet`, { walletAddress: address }, {
        params: { initData: WebApp.initData },
        headers: { "x-api-key": APIKey },
      }).catch(err => console.error("Failed to securely link TON wallet:", err));
    }
  }, [wallet, userData.userId, userData.user?.userId, APIKey]);

  // Parses the Telegram startapp parameter.
  // Supports two intents:
  //   channel_<id>  → deep link to a specific channel (stored in sessionStorage)
  //   <uuid>        → referral code (legacy: no prefix)
  const parseStartParam = () => {
    const startParam =
      WebApp.initDataUnsafe?.start_param ||
      new URLSearchParams(window.location.search).get("tgWebAppStartParam") ||
      "";

    if (startParam === "football") {
      sessionStorage.setItem("deepLinkRoute", "/game/football");
      return null;
    }

    if (startParam.startsWith("channel_")) {
      // Store the channel ID for Home.js to pick up after channels load
      sessionStorage.setItem("deepLinkChannelId", startParam.replace("channel_", ""));
      return null; // not a referral code
    }
    return startParam || null;
  };

  const getDeviceId = async () => {
    // Signal 1: stable localStorage UUID — generated once, persisted forever
    let localId = null;
    try {
      localId = localStorage.getItem('_bsc_device_id');
      if (!localId) {
        localId = crypto.randomUUID();
        localStorage.setItem('_bsc_device_id', localId);
      }
    } catch (err) {
      console.error("Error reading localStorage device ID:", err);
    }

    // Signal 2: FingerprintJS visitorId — catches users who clear localStorage
    let fpId = null;
    try {
      const fp = await fpPromise.load();
      const result = await fp.get();
      fpId = result.visitorId;
    } catch (err) {
      console.error("Error generating fingerprint:", err);
    }

    return { localId, fpId };
  };

  const fetchUserData = async (tgUser, referrerCode) => {
    const { localId, fpId } = await getDeviceId();
    try {
      const response = await axios.get(`${API_URL}/api/user/${tgUser.id}`, {
        params: { initData: WebApp.initData, deviceId: localId, fpId },
        headers: { "x-api-key": APIKey },
      });
      setUserData((prev) => ({ ...prev, user: response.data }));
      setLoading(false);
      const updatedData = await checkAndUpdateUserInfo(tgUser, response);
      if (updatedData) {
        setUserData((prev) => ({ ...prev, user: updatedData }));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        await axios.post(
          `${API_URL}/api/users`,
          {
            userId: tgUser.id,
            username: tgUser.username,
            firstName: tgUser.first_name,
            lastName: tgUser.last_name,
            isPremium: tgUser.is_premium,
            photo_url: tgUser.photo_url,
            chatId: tgUser.id,
            referrerCode,
            initData: WebApp.initData,
            deviceId: localId,
            fpId,
          },
          {
            params: { initData: WebApp.initData },
            headers: { "x-api-key": APIKey },
          }
        );
        const newUserResponse = await axios.get(
          `${API_URL}/api/user/${tgUser.id}`,
          {
            params: { initData: WebApp.initData },
            headers: { "x-api-key": APIKey },
          }
        );
        setUserData((prev) => ({ ...prev, user: newUserResponse.data }));
        setLoading(false);
      } else {
        console.error("Error retrieving user data:", error);
        setLoading(false);
      }
    }
  };

  // Refresh user data from the server (for use after mutations)
  const refreshUser = async () => {
    const userId = userData.userId || userData.user?.userId;
    if (!userId) return;
    try {
      const { localId, fpId } = await getDeviceId();
      const response = await axios.get(`${API_URL}/api/user/${userId}`, {
        params: { initData: WebApp.initData, timestamp: Date.now(), deviceId: localId, fpId },
        headers: { "x-api-key": APIKey },
      });
      setUserData((prev) => ({ ...prev, user: response.data }));
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  useEffect(() => {
    if (effectRan.current) return;

    WebApp.ready();
    WebApp.disableVerticalSwipes();
    WebApp.enableClosingConfirmation();
    WebApp.expand();

    setUserData((prev) => ({
      ...prev,
      referralCode: parseStartParam(),
    }));

    if (WebApp.initDataUnsafe?.user) {
      setIsTelegramMiniApp(true);
      const tgUser = WebApp.initDataUnsafe.user;
      setUserData((prev) => ({
        ...prev,
        username: tgUser.username,
        firstName: tgUser.first_name,
        lastName: tgUser.last_name,
        photo_url: tgUser.photo_url,
        isPremium: tgUser.is_premium,
        userId: tgUser.id,
      }));
      fetchUserData(tgUser, parseStartParam());
    } else {
      setLoading(false);
    }

    effectRan.current = true;
    return () => { effectRan.current = true; };
  }, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        loading,
        setLoading,
        isBanned,
        setIsBanned,
        isTelegramMiniApp,
        APIKey,
        refreshUser,
        headerHidden,
        setHeaderHidden,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export default UserContext;
